import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@dist-marketplace-frontend/services';
import { Notification } from 'toolkit';

export type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

const DefaultFallback = () => (
  <Notification>
    Oops, something went wrong loading this part of the page. Please try again
    later
  </Notification>
);

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  // TODO: Add React.captureOwnerStack() to customData when upgrading to React 19 for richer dev-time stack traces.
  // https://react.dev/reference/react/Component#componentdidcatch
  override componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error({
      error,
      message: 'Error caught by error boundary',
      customData: {
        componentStack: info.componentStack,
      },
    });
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
